import React, { useRef, useEffect } from 'react';

let gsiLoadPromise = null;
function loadGsiScript() {
  if (typeof window !== 'undefined' && window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (gsiLoadPromise) return gsiLoadPromise;
  gsiLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gsiLoadPromise;
}

/**
 * Google Identity Services button. Calls onCredential(googleJwt) when user completes sign-in.
 */
const GoogleSignInButton = ({ onCredential, disabled }) => {
  const divRef = useRef(null);
  const cbRef = useRef(onCredential);
  cbRef.current = onCredential;

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId || !divRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        await loadGsiScript();
        if (cancelled || !divRef.current) return;
        const el = divRef.current;
        el.innerHTML = '';
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (res) => {
            if (res?.credential) cbRef.current(res.credential);
          },
          auto_select: false
        });
        window.google.accounts.id.renderButton(el, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 320,
          locale: 'en'
        });
      } catch (e) {
        console.error('Google Sign-In init failed', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) {
    return (
      <p className="text-xs text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        Google sign-in is not configured (set REACT_APP_GOOGLE_CLIENT_ID).
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center w-full">
      <div
        ref={divRef}
        className={`min-h-[44px] flex items-center justify-center ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
        aria-hidden={disabled}
      />
    </div>
  );
};

export default GoogleSignInButton;
